import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Typography } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { HOTQUU_ACCESS_DENIED_MESSAGE, userHasHotQuuAccess } from './utils/hotQuuAccess';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import LoadingSpinner from './components/LoadingSpinner';
import StationSelection from './components/StationSelection';

// Dark UI shell + cyan primary (Figma HOT QUUS)
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#37a2d9',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#0b0e12',
      paper: '#141a23',
    },
    text: {
      primary: '#e8eef4',
      secondary: 'rgba(232, 238, 244, 0.72)',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
      },
    },
  },
});

// HotQuus access permissions (#29).
const HotQuusAccessRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner message="Checking authentication..." />;
  }

  if (user && !userHasHotQuuAccess(user)) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, backgroundColor: '#0b0e12' }}>
        <Typography variant="h6" sx={{ color: '#e8eef4', textAlign: 'center' }}>
          {HOTQUU_ACCESS_DENIED_MESSAGE}
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, hasSelectedStation } = useAuth();
  const stationInStorage = typeof window !== 'undefined' && !!localStorage.getItem('selectedStation');
  const stationOk = hasSelectedStation || stationInStorage;

  if (loading) {
    return <LoadingSpinner message="Checking authentication..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!stationOk) {
    return <Navigate to="/select-station" replace />;
  }

  return <>{children}</>;
};

// Station Selection Route component
const StationSelectionRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, hasSelectedStation } = useAuth();

  if (loading) {
    return <LoadingSpinner message="Checking authentication..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (hasSelectedStation) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Public Route component (redirects to dashboard if already authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, hasSelectedStation } = useAuth();

  if (loading) {
    return <LoadingSpinner message="Checking authentication..." />;
  }

  if (isAuthenticated) {
    return hasSelectedStation ? <Navigate to="/dashboard" replace /> : <Navigate to="/select-station" replace />;
  }

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const { setStationSelected } = useAuth();

  const handleStationSelected = () => {
    setStationSelected();
  };

  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/select-station"
          element={
            <StationSelectionRoute>
              <StationSelection onStationSelected={handleStationSelected} />
            </StationSelectionRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <HotQuusAccessRoute>
                <Dashboard />
              </HotQuusAccessRoute>
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
